import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PAGE_CONFIG } from './Config';
import EmptyPage from '../../components/EmptyPage';
import { useTranslation } from 'react-i18next';
import PageIntro from '../../components/PageIntro';
import ContentCard from '../../components/ContentCard';
import TablePagination from '../../components/Pagination';
import tableStyles from '../../styles/tableStyles';
import api from '../../api';
import { useQuery } from '@tanstack/react-query';

import { isLocalCourtsType } from '../../catalog/catalogGroups';

const Cases = () => {
    const { t } = useTranslation();
    const tr = useCallback(
        (key) => {
            return t(`cases:${key}`);
        },
        [t]
    );

    const pageConfig = useMemo(() => PAGE_CONFIG(tr), [tr]);

    const [materialTypeId, setMaterialTypeId] = useState(null);
    
    // Pagination state management
    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: ''
    });

    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setParams((prev) => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (limit) => {
        if (limit < 1) return;
        setParams((prev) => ({ ...prev, limit }));
    };

    // get material types
    const { data: materialTypes } = useQuery({
        queryKey: ['materialTypes'],
        queryFn: () => api.materialTypes.getMaterialTypes(),
        select: (data) => data?.data?.data
    });

    useEffect(() => {
        if (materialTypes && materialTypes.length > 0) {
            const materialType = materialTypes.find((i) => isLocalCourtsType(i.name));
            if (materialType) {
                setMaterialTypeId(materialType.id);
            } else {
                console.warn('No Local Courts Cases material type found. Available types:', materialTypes.map((mt) => mt.name));
            }
        }
    }, [materialTypes]);

    // Combine materialTypeId with other params
    const queryParams = useMemo(
        () => ({
            ...params,
            materialTypeId: materialTypeId
        }),
        [params, materialTypeId]
    );

    // Use useQuery with proper pagination handling
    const { data: casesResponse, isLoading } = useQuery({
        queryKey: ['cases', queryParams],
        queryFn: () => api.upload.getUploads(queryParams),
        enabled: !!materialTypeId, // Only run when materialTypeId is available
        select: (data) => data?.data
    });

    // Extract data and pagination from response
    const cases = casesResponse?.data;
    const pagination = {
        page: params.page,
        limit: params.limit,
        totalCount: casesResponse?.totalCount || 0
    };
    const totalPages = Math.ceil(pagination.totalCount / pagination.limit);

    return (
        <div>
            <PageIntro description={pageConfig.PAGE_DESCRIPTION} />
            <div className="text-sm text-gray-500 ml-2 mt-4 dark:text-gray-400">
                {tr('showing')} {cases?.length} {tr('of')} {pagination.totalCount}
            </div>

            {cases && cases.length > 0 ? (
                <ContentCard data={cases} />
            ) : (
                <EmptyPage colSpan={12} isLoading={isLoading} icon={pageConfig.EMPTY_PAGE_ICON} description={pageConfig.SEARCH_DESCRIPTION} placeholder={pageConfig.SEARCH_PLACEHOLDER} loadingMessage={pageConfig.SEARCH_LOADING_MESSAGE} />
            )}

            {/* Add pagination component */}
            {cases && cases.length > 0 && <TablePagination totalPages={totalPages} page={pagination.page} limit={pagination.limit} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} tableStyles={tableStyles} />}
        </div>
    );
};

export default Cases;
